import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, type FormEvent } from 'react';
import { ApiError, api } from '@/api';
import { queryKeys } from '@/api/query-keys';
import { AtlasLogo, Button, Card, Icon, Input } from '@/ui';
import { FUNCTIONS, PRIMARY_ROLES, isEarlensEmail } from '@/config/roles';
import type { User } from '@/types/api';

const selectStyle: React.CSSProperties = {
  height: 'var(--apm-input-h)',
  padding: '0 12px',
  background: 'var(--apm-bg-2)',
  color: 'var(--apm-ink)',
  border: '1px solid var(--apm-line)',
  borderRadius: 'var(--apm-radius)',
  fontFamily: 'var(--apm-font-body)',
  fontSize: 13.5,
  width: '100%',
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'register'>('email');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  const onAuthed = (user: User) => {
    queryClient.setQueryData(queryKeys.auth.me, user);
    navigate(redirectTo, { replace: true });
  };

  const login = useMutation({
    mutationFn: () => api.auth.login(email.trim().toLowerCase()),
    onSuccess: onAuthed,
    onError: (err) => {
      // Unknown-but-valid email → move to the self-registration step.
      if (err instanceof ApiError && err.status === 404) setStep('register');
    },
  });

  const domainOk = isEarlensEmail(email);

  const onSubmitEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!domainOk || login.isPending) return;
    login.mutate();
  };

  const emailError =
    email && !domainOk
      ? 'Use your @earlens.com email address.'
      : login.error instanceof ApiError && login.error.status !== 404
        ? (login.error.status === 400
            ? 'Use your @earlens.com email address.'
            : login.error.status === 403
              ? 'This account has been deactivated.'
              : login.error.message ?? 'Could not sign in.')
        : login.error && !(login.error instanceof ApiError)
          ? 'Could not sign in. Check your connection.'
          : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--apm-bg)',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 400 }} pad={false}>
        <div style={{ padding: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'var(--apm-ink)',
                color: 'var(--apm-bg-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <AtlasLogo size={26} />
            </div>
            <div>
              <div
                style={{
                  fontFamily: 'var(--apm-font-display)',
                  fontSize: 22,
                  lineHeight: 1,
                  letterSpacing: '-0.01em',
                  color: 'var(--apm-ink)',
                }}
              >
                Atlas
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  color: 'var(--apm-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  marginTop: 3,
                }}
              >
                AI Portfolio Manager
              </div>
            </div>
          </div>

          {step === 'email' ? (
            <>
              <h1 style={headingStyle}>Sign in</h1>
              <p style={subStyle}>
                Enter your Earlens email — no password needed. Azure AD coming for deployment.
              </p>
              <form onSubmit={onSubmitEmail} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Input
                  type="email"
                  autoComplete="username"
                  placeholder="you@earlens.com"
                  value={email}
                  onChange={setEmail}
                  prefix={<Icon name="user" size={14} />}
                />
                {emailError && <ErrorBox>{emailError}</ErrorBox>}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!domainOk || login.isPending}
                  style={{ marginTop: 4, justifyContent: 'center', width: '100%' }}
                >
                  {login.isPending ? 'Signing in…' : 'Continue'}
                </Button>
              </form>
            </>
          ) : (
            <RegisterStep
              email={email.trim().toLowerCase()}
              onBack={() => setStep('email')}
              onAuthed={onAuthed}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

function RegisterStep({
  email,
  onBack,
  onAuthed,
}: {
  email: string;
  onBack: () => void;
  onAuthed: (user: User) => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [func, setFunc] = useState('');

  const register = useMutation({
    mutationFn: () =>
      api.auth.register({
        email,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role,
        function: func || null,
      }),
    onSuccess: onAuthed,
  });

  const canSubmit =
    firstName.trim().length > 0 && lastName.trim().length > 0 && role !== '' && !register.isPending;

  return (
    <>
      <h1 style={headingStyle}>Welcome — let's set you up</h1>
      <p style={subStyle}>
        We don't have an account for <strong>{email}</strong> yet. Tell us who you are.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input placeholder="First name" value={firstName} onChange={setFirstName} />
          <Input placeholder="Last name" value={lastName} onChange={setLastName} />
        </div>
        <div>
          <div style={labelStyle}>Primary role *</div>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={selectStyle}>
            <option value="">Select a role…</option>
            {PRIMARY_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={labelStyle}>Function (optional)</div>
          <select value={func} onChange={(e) => setFunc(e.target.value)} style={selectStyle}>
            <option value="">None</option>
            {FUNCTIONS.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        {register.isError && (
          <ErrorBox>
            {register.error instanceof ApiError ? register.error.message : 'Could not create your account.'}
          </ErrorBox>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Button variant="ghost" onClick={onBack} disabled={register.isPending}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={() => register.mutate()}
            disabled={!canSubmit}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            {register.isPending ? 'Creating…' : 'Create account & sign in'}
          </Button>
        </div>
      </div>
    </>
  );
}

const headingStyle: React.CSSProperties = {
  margin: '0 0 6px',
  fontFamily: 'var(--apm-font-display)',
  fontWeight: 400,
  fontSize: 'var(--apm-text-2xl)',
  letterSpacing: '-0.01em',
  color: 'var(--apm-ink)',
};
const subStyle: React.CSSProperties = {
  margin: '0 0 24px',
  fontSize: 13,
  color: 'var(--apm-muted)',
  lineHeight: 1.5,
};
const labelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--apm-muted)',
  fontWeight: 500,
  marginBottom: 6,
};

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        fontSize: 12.5,
        color: 'var(--apm-risk)',
        background: 'var(--apm-risk-soft)',
        padding: '8px 12px',
        borderRadius: 'var(--apm-radius)',
      }}
    >
      {children}
    </div>
  );
}
